import React, { useMemo, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Btn, Chip, Notice, Label, Empty } from "./ui/index.jsx";
import { REGISTERS } from "../lib/registers.js";
import { applyMapping, describeTargets, IMPORTABLE } from "../lib/importers/mapping.js";
import { mapWorkbook } from "../lib/importers/mapWorkbook.js";
import { groupNames, renameMap, applyNames } from "../lib/importers/people.js";
import { writeRegister } from "../lib/importers/writeRegister.js";

/* Putting a real workbook into the registers.
 *
 * The order is deliberate and it is the whole point of the screen: propose,
 * show, correct, preview, then save. Nothing is written until somebody has
 * looked at the last two. An importer that saves first and reports afterwards
 * is an importer nobody will risk 389 rows on.
 */

const input = {
  padding: "5px 8px", border: `1px solid ${T.line}`, borderRadius: 4,
  fontSize: 12.5, background: "#fff", color: T.ink, maxWidth: "100%",
};

const cell = { padding: "5px 8px", fontSize: 12, borderBottom: `1px solid ${T.line}`,
               textAlign: "left", verticalAlign: "top" };
const head = { ...cell, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em",
               textTransform: "uppercase", color: T.navy,
               borderBottom: `2px solid ${T.navy}`, whiteSpace: "nowrap" };

/* The first few rows as they would be saved. Reading a preview is the only
   way anybody can tell a right mapping from a plausible one. */
function Preview({ rows, fields }) {
  const shown = fields.filter((f) => rows.some((r) => r[f.key] !== undefined && r[f.key] !== "" && r[f.key] !== null));
  if (!rows.length) return <Empty text="Nothing would be saved from this sheet." />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
        <thead><tr>{shown.map((f) => <th key={f.key} style={head}>{f.label}</th>)}</tr></thead>
        <tbody>
          {rows.slice(0, 5).map((r, i) => (
            <tr key={i}>
              {shown.map((f) => (
                <td key={f.key} style={{ ...cell, maxWidth: 230, overflowWrap: "break-word" }}>
                  {r[f.key] === null || r[f.key] === "" ? (
                    <span style={{ color: T.soft }}>blank</span>
                  ) : String(r[f.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SheetPanel({ sheet, mapping, contract, onChange }) {
  const register = mapping.register;
  const definition = register ? REGISTERS[register] : null;
  const fields = useMemo(
    () => (definition ? definition.fields(contract) : []),
    [definition, contract],
  );

  const applied = useMemo(
    () => (register ? applyMapping(sheet, mapping, { contract }) : { rows: [], issues: [] }),
    [sheet, mapping, contract, register],
  );

  /* Owners only. A column of school names or vendors has its own spellings
     and its own answers, and guessing at those is a different job. */
  const owners = useMemo(() => {
    if (!mapping.columns?.owner) return [];
    return groupNames(applied.rows.map((r) => r.owner)).filter((g) => g.variants.length > 1);
  }, [applied.rows, mapping.columns?.owner]);

  const setColumn = (field, column) =>
    onChange({ ...mapping, columns: { ...mapping.columns, [field]: column || undefined } });

  const taken = new Set(Object.values(mapping.columns ?? {}));

  return (
    <Panel
      title={sheet.name}
      accent={register ? undefined : GOLD_INK}
      right={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {mapping.confidence === "low" && register && <Chip color={GOLD_INK}>Unsure</Chip>}
          <Chip color={T.steel}>{sheet.rows.length} rows</Chip>
          <select style={input} value={register}
            onChange={(e) => onChange({ ...mapping, register: e.target.value })}>
            <option value="">Do not import this sheet</option>
            {IMPORTABLE.filter((k) => REGISTERS[k]).map((k) => (
              <option key={k} value={k}>{REGISTERS[k].label}</option>
            ))}
          </select>
        </div>
      }
    >
      {mapping.reason && (
        <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 10, lineHeight: 1.6 }}>
          {mapping.reason}
        </div>
      )}

      {!register ? null : (
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div style={{ marginBottom: 6 }}><Label>Which column is which</Label></div>
            <div style={{ display: "grid", gap: 6,
                          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
              {fields.map((f) => (
                <div key={f.key} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: T.ink, minWidth: 96 }}>
                    {f.label}{f.required && <span style={{ color: "#8F2B25" }}> *</span>}
                  </span>
                  <select style={{ ...input, flex: 1, minWidth: 0 }}
                    value={mapping.columns?.[f.key] ?? ""}
                    onChange={(e) => setColumn(f.key, e.target.value)}>
                    <option value="">Not imported</option>
                    {sheet.columns.map((c) => (
                      <option key={c.key} value={c.key}
                        disabled={taken.has(c.key) && mapping.columns?.[f.key] !== c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {applied.issues.length > 0 && (
            <div style={{ display: "grid", gap: 6 }}>
              {applied.issues.map((issue, i) => (
                <Notice key={i} kind="warn">
                  {issue.kind === "blank" ? issue.detail : issue.kind === "register" ? issue.detail : (
                    <>
                      {issue.count} value{issue.count === 1 ? "" : "s"} in <b>{issue.label}</b>{" "}
                      could not be read as a {fields.find((f) => f.key === issue.field)?.type ?? "value"}
                      {" "}and will be saved blank: {issue.examples.join(", ")}.
                    </>
                  )}
                </Notice>
              ))}
            </div>
          )}

          {owners.length > 0 && (
            <div>
              <div style={{ marginBottom: 6 }}><Label color={GOLD_INK}>These look like the same person</Label></div>
              <div style={{ fontSize: 12, color: T.soft, marginBottom: 8, lineHeight: 1.6 }}>
                Nothing is changed unless you tick it. Two people really can share
                a surname, so this is a suggestion and not a rule.
              </div>
              <div style={{ display: "grid", gap: 5 }}>
                {owners.map((g) => (
                  <label key={g.canonical} style={{
                    display: "flex", gap: 9, alignItems: "start", fontSize: 12.5,
                    padding: "6px 9px", background: T.chip, borderRadius: 4, cursor: "pointer",
                  }}>
                    <input type="checkbox" style={{ marginTop: 3 }}
                      checked={Boolean(mapping.mergeNames?.[g.canonical])}
                      onChange={(e) => onChange({
                        ...mapping,
                        mergeNames: { ...mapping.mergeNames, [g.canonical]: e.target.checked },
                      })} />
                    <span>
                      <b style={{ color: T.navy }}>{g.canonical}</b>{" "}
                      <span style={{ color: T.soft }}>({g.count} rows)</span>
                      <div style={{ color: T.soft, marginTop: 2 }}>
                        {g.variants.map((v) => `${v.name} (${v.count})`).join("  ·  ")}
                      </div>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <div style={{ marginBottom: 6 }}>
              <Label>What would be saved · first 5 of {applied.rows.length}</Label>
            </div>
            <Preview rows={applied.rows} fields={fields} />
          </div>
        </div>
      )}
    </Panel>
  );
}

export default function RegisterImport({ contract, workbook, userId, onDone }) {
  const [mappings, setMappings] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [beats, setBeats] = useState(0);
  const [mode, setMode] = useState("add");
  const [saved, setSaved] = useState(null);

  const usable = useMemo(() => workbook.sheets.filter((s) => s.usable), [workbook]);

  const propose = async () => {
    setError(""); setBeats(0); setBusy("mapping");
    try {
      const found = await mapWorkbook({
        contractId: contract.id, contract, sheets: usable,
        onProgress: () => setBeats((n) => n + 1),
      });
      const byName = new Map(found.map((m) => [m.name, m]));
      setMappings(usable.map((s) => ({
        name: s.name,
        register: byName.get(s.name)?.register ?? "",
        reason: byName.get(s.name)?.reason ?? "",
        confidence: byName.get(s.name)?.confidence ?? "high",
        columns: byName.get(s.name)?.columns ?? {},
        constants: byName.get(s.name)?.constants ?? {},
        mergeNames: {},
      })));
    } catch (failed) {
      setError(failed.message || "The columns could not be matched.");
    } finally {
      setBusy("");
    }
  };

  const chosen = (mappings ?? []).filter((m) => m.register);

  const save = async () => {
    setError(""); setBusy("saving");
    const results = [];
    for (const mapping of chosen) {
      const sheet = usable.find((s) => s.name === mapping.name);
      const { rows } = applyMapping(sheet, mapping, { contract });
      const accepted = new Set(
        Object.entries(mapping.mergeNames ?? {}).filter(([, on]) => on).map(([name]) => name),
      );
      const named = accepted.size
        ? applyNames(rows, "owner", renameMap(groupNames(rows.map((r) => r.owner)), accepted))
        : rows;

      const out = await writeRegister({
        register: mapping.register, contractId: contract.id, rows: named,
        userId, mode,
      });
      if (!out.ok) {
        setBusy("");
        setError(`${REGISTERS[mapping.register].label}: ${out.error}`);
        if (results.length) setSaved(results);
        return;
      }
      results.push({ register: mapping.register, ...out });
    }
    setBusy("");
    setSaved(results);
    onDone?.();
  };

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <Panel
        title="Import into the registers"
        right={mappings && (
          <select style={input} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="add">Add everything as new rows</option>
            <option value="merge">Update rows with the same item number</option>
          </select>
        )}
      >
        <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7 }}>
          The rows never leave this browser. Only the column names and a few sample
          values are sent, and what comes back is a mapping saying which column is
          which. Correct anything that is wrong, look at the preview, then save.
          {mode === "merge" && (
            <>
              <br /><br />
              Updating matches on the item number. A row whose number is already in
              the register is updated. Anything else is added. A number that appears
              twice is added rather than overwritten, because choosing which one to
              overwrite would be a guess.
            </>
          )}
        </div>

        {error && <div style={{ marginTop: 12 }}><Notice kind="error">{error}</Notice></div>}

        {busy === "mapping" && (
          <div style={{ marginTop: 12 }}>
            <Notice>Working out which column is which{".".repeat((beats % 3) + 1)}</Notice>
          </div>
        )}
        {busy === "saving" && (
          <div style={{ marginTop: 12 }}><Notice>Saving...</Notice></div>
        )}

        {saved && (
          <div style={{ marginTop: 12 }}>
            <Notice kind="ok">
              {saved.map((r) => (
                <div key={r.register}>
                  {REGISTERS[r.register].label}: {r.added} added
                  {r.updated ? `, ${r.updated} updated` : ""}
                  {r.ambiguous?.length
                    ? `. ${r.ambiguous.length} item number${r.ambiguous.length === 1 ? " was" : "s were"} already in the register more than once, so those were added rather than overwritten.`
                    : "."}
                </div>
              ))}
            </Notice>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {!mappings ? (
            <Btn kind="gold" disabled={Boolean(busy) || !usable.length} onClick={propose}>
              {busy === "mapping" ? "Working..." : "Work out the columns"}
            </Btn>
          ) : (
            <>
              <Btn kind="gold" disabled={Boolean(busy) || !chosen.length} onClick={save}>
                {busy === "saving" ? "Saving..." : `Save ${chosen.length} sheet${chosen.length === 1 ? "" : "s"}`}
              </Btn>
              <Btn kind="ghost" disabled={Boolean(busy)} onClick={propose}>Work them out again</Btn>
            </>
          )}
        </div>
      </Panel>

      {(mappings ?? []).map((mapping, i) => (
        <SheetPanel
          key={mapping.name}
          sheet={usable.find((s) => s.name === mapping.name)}
          mapping={mapping}
          contract={contract}
          onChange={(next) => setMappings(mappings.map((m, j) => (j === i ? next : m)))}
        />
      ))}
    </div>
  );
}
